import { type Instant, parseInstant } from '@taler/domain'
import type { OperationQuery, Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, ledgerKey, principalKey } from '../http/keys.ts'
import { projectQuery, type QueryProjection, queryFingerprint } from '../http/query.ts'
import type { Page } from './page.ts'

export type NotificationType = Schemas['NotificationResponse']['type']
export type NotificationEmailStatus = NonNullable<Schemas['NotificationResponse']['emailStatus']>
export type NotificationChannel = Schemas['NotificationPreferenceResponse']['channel']

export interface Notification {
  readonly id: string
  readonly type: NotificationType
  readonly title: string
  readonly body: string
  readonly ledgerId: string | undefined
  readonly locale: 'en' | 'hu'
  readonly emailStatus: NotificationEmailStatus | undefined
  readonly emailPossiblyDuplicated: boolean
  readonly createdAt: Instant
  readonly readAt: Instant | undefined
}

export interface NotificationPreference {
  readonly type: NotificationType
  readonly channel: NotificationChannel
  readonly enabled: boolean
  readonly mandatory: boolean
  readonly source: 'DEFAULT' | 'OVERRIDE'
}

export interface NotificationPreferences {
  readonly preferences: readonly NotificationPreference[]
  readonly showAmounts: Readonly<Record<NotificationChannel, boolean>>
}

export interface NotificationPreferenceOverride {
  readonly type: NotificationType
  readonly channel: NotificationChannel
  readonly enabled: boolean
}

export interface UpdateNotificationPreferences {
  readonly overrides: readonly NotificationPreferenceOverride[]
  readonly showAmounts: Readonly<Record<NotificationChannel, boolean>>
}

export type NotificationListQuery = OperationQuery<'me_notifications'>

const NOTIFICATION_LIST_PROJECTION: QueryProjection<NotificationListQuery> = {
  limit: (value) => String(value),
  cursor: (value) => value,
  unreadOnly: (value) => String(value),
}

export const notificationKeys = {
  list: (query: NotificationListQuery): CacheKey =>
    principalKey(
      'notifications',
      'list',
      queryFingerprint(projectQuery(query, NOTIFICATION_LIST_PROJECTION)),
    ),
  unreadCount: (): CacheKey => principalKey('notifications', 'unread-count'),
  preferences: (ledgerId: string): CacheKey => ledgerKey(ledgerId, 'notification-preferences'),
  /** A mark-read only ever narrows the unread set, never widens it - every list query is stale. */
  invalidatedByRead: (): readonly CacheKey[] => [principalKey('notifications')],
} as const

export interface NotificationsRepository {
  list(query?: NotificationListQuery, signal?: AbortSignal): Promise<Page<Notification>>
  markRead(notificationId: string): Promise<Notification>
  unreadCount(signal?: AbortSignal): Promise<number>
  preferences(ledgerId: string, signal?: AbortSignal): Promise<NotificationPreferences>
  replacePreferences(
    ledgerId: string,
    update: UpdateNotificationPreferences,
  ): Promise<NotificationPreferences>
}

export function createNotificationsRepository(client: TalerClient): NotificationsRepository {
  const base = '/api/v1/me/notifications'

  return {
    async list(query = {}, signal) {
      const response = await client.call<Schemas['PageResponseNotificationResponse']>({
        method: 'GET',
        path: base,
        query: projectQuery(query, NOTIFICATION_LIST_PROJECTION),
        ...(signal === undefined ? {} : { signal }),
      })
      return { items: response.items.map(toNotification), nextCursor: response.nextCursor }
    },

    async markRead(notificationId) {
      return toNotification(
        await client.call<Schemas['NotificationResponse']>({
          method: 'POST',
          path: `${base}/${encodeURIComponent(notificationId)}/read`,
        }),
      )
    },

    async unreadCount(signal) {
      const response = await client.call<Schemas['UnreadCountResponse']>({
        method: 'GET',
        path: `${base}/unread-count`,
        ...(signal === undefined ? {} : { signal }),
      })
      return response.count
    },

    async preferences(ledgerId, signal) {
      const response = await client.call<Schemas['NotificationPreferencesResponse']>({
        method: 'GET',
        path: `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/notification-preferences`,
        ...(signal === undefined ? {} : { signal }),
      })
      return toPreferences(response)
    },

    async replacePreferences(ledgerId, update) {
      const response = await client.call<
        Schemas['NotificationPreferencesResponse'],
        Schemas['UpdateNotificationPreferencesRequest']
      >({
        method: 'PUT',
        path: `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/notification-preferences`,
        body: {
          overrides: update.overrides.map((override) => ({
            type: override.type,
            channel: override.channel,
            enabled: override.enabled,
          })),
          showAmounts: { ...update.showAmounts },
        },
      })
      return toPreferences(response)
    },
  }
}

function toNotification(response: Schemas['NotificationResponse']): Notification {
  return {
    id: response.id,
    type: response.type,
    title: response.title,
    body: response.body,
    ledgerId: response.ledgerId,
    locale: response.locale,
    emailStatus: response.emailStatus,
    emailPossiblyDuplicated: response.emailPossiblyDuplicated,
    createdAt: parseInstant(response.createdAt),
    readAt: response.readAt === undefined ? undefined : parseInstant(response.readAt),
  }
}

function toPreferences(response: Schemas['NotificationPreferencesResponse']): NotificationPreferences {
  return {
    preferences: response.preferences.map((preference) => ({
      type: preference.type,
      channel: preference.channel,
      enabled: preference.enabled,
      mandatory: preference.mandatory,
      source: preference.source,
    })),
    showAmounts: response.showAmounts as Record<NotificationChannel, boolean>,
  }
}
